import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import axiosJwtInterceptor from "./axiosJwtInterceptor";
import axiosErrorInterceptor from "./axiosErrorInterceptor";
import axiosSuccessInterceptor from "./axiosSuccessInterceptor";
import fetchWithDeduplication from "../helpers/fetchWithDeduplication";

const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

type AppName = "auth" | "responder" | "coordinator" | "detector";

const appBaseUrls: Record<AppName, string | undefined> = {
  auth: process.env.REACT_APP_API_AUTH_BASE_URL,
  responder: process.env.REACT_APP_API_RESPONDER_BASE_URL,
  coordinator: process.env.REACT_APP_API_GMD_BASE_URL,
  detector: process.env.REACT_APP_API_BASE_URL,
};

function deduplicateAxiosInstance(axiosInstance: AxiosInstance) {
  const origGet = axiosInstance.get.bind(axiosInstance);

  axiosInstance.get = function <T = any, R = AxiosResponse<T>, D = any>(
    url: string,
    config?: AxiosRequestConfig<D>,
  ): Promise<R> {
    const key = `get:${url}:${JSON.stringify(config || {})}`;
    return fetchWithDeduplication(key, () => origGet(url, config));
  };
}

function configureAxiosInstance(app: AppName): AxiosInstance {
  const headers: Record<string, string> = {
    "Content-type": "application/json",
  };

  if (app === "detector") {
    headers["X-TimeZone"] = userTimeZone;
  }

  const instance = axios.create({
    baseURL: appBaseUrls[app],
    headers,
  });

  deduplicateAxiosInstance(instance);

  instance.interceptors.request.use(axiosJwtInterceptor, (e) =>
    Promise.reject(e),
  );

  instance.interceptors.response.use(
    (response) => axiosSuccessInterceptor(response),
    (error) => axiosErrorInterceptor(error),
  );

  return instance;
}

export function createHttpClients(apps: AppName[]) {
  return apps.reduce(
    (clients, app) => {
      clients[app] = configureAxiosInstance(app);
      return clients;
    },
    {} as Record<AppName, AxiosInstance>,
  );
}

const clients = createHttpClients([
  "detector",
  "auth",
  "responder",
  "coordinator",
]);

const http = clients.detector;

export const authHttp = clients.auth;
export const responderHttp = clients.responder;
export const coordinatorHttp = clients.coordinator;

export default http;
